import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Loader2, Wallet, Receipt, FileText, TrendingUp, TrendingDown,
  AlertCircle, Building2, ArrowUpRight, Percent, BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import ModuleAutomationsPanel from '../components/automation/ModuleAutomationsPanel';

const API = process.env.REACT_APP_BACKEND_URL || '';
const fmt = (n, cur = 'PHP') =>
  new Intl.NumberFormat('en-PH', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(n || 0);

function Stat({ label, value, accent = 'emerald', icon: Icon, hint }) {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
  };
  return (
    <Card className={`border ${tones[accent]} bg-white/60`} data-testid={`finance-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
          {Icon && <Icon className="h-4 w-4 text-gray-400" />}
        </div>
        <div className="text-xl sm:text-2xl font-bold">{value}</div>
        {hint && <div className="text-[10px] text-gray-500 mt-1">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function QuickLink({ to, icon: Icon, label, hint, badge }) {
  return (
    <Link
      to={to}
      data-testid={`finance-quicklink-${to.split('/').pop()}`}
      className="group flex items-start gap-3 p-4 rounded-lg border border-gray-200 bg-white hover:border-gray-900 hover:shadow-sm transition"
    >
      <div className="h-9 w-9 rounded-md bg-gray-50 group-hover:bg-gray-900 group-hover:text-white flex items-center justify-center transition">
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900">{label}</span>
          {badge && <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">{badge}</span>}
        </div>
        <p className="text-xs text-gray-500 mt-0.5 truncate">{hint}</p>
      </div>
      <ArrowUpRight className="h-4 w-4 text-gray-300 group-hover:text-gray-900 transition" />
    </Link>
  );
}

export default function FinancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: dash } = await axios.get(`${API}/api/finance/dashboard`);
        setData(dash);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load finance data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="page-shell-wide flex items-center justify-center min-h-[40vh]"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  if (!data) return <div className="page-shell-wide text-center text-sm text-gray-500">Could not load finance data.</div>;

  const pl = data.profit_loss;
  const bs = data.balance_sheet;
  const aging = data.receivables_aging;
  const payables = data.payables_aging || { buckets: { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 }, total_outstanding: 0 };
  const cf = data.cash_flow;
  const tax = data.tax_summary;
  const netIncomeAccent = pl.net_income >= 0 ? 'emerald' : 'rose';

  return (
    <div className="page-shell-wide" data-testid="finance-page">
      <div className="mb-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-400 mb-1">
          <Wallet className="h-3 w-3" /> Finance
        </div>
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Finance Dashboard<PageInfoTooltip pageKey="finance-dashboard" /></h1>
        <p className="text-sm text-gray-600 mt-1">Profit, cash position, accounts receivable, payables, and tax — all in one place.</p>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat label="Revenue (MTD)" value={fmt(pl.revenue)} accent="emerald" icon={TrendingUp} hint={`${pl.period.start} → ${pl.period.end}`} />
        <Stat label="Expenses (MTD)" value={fmt(pl.total_expenses)} accent="rose" icon={TrendingDown} />
        <Stat label="Net Income (MTD)" value={fmt(pl.net_income)} accent={netIncomeAccent} icon={Wallet} hint={`${(pl.profit_margin * 100).toFixed(1)}% margin`} />
        <Stat label="Cash" value={fmt(bs.assets.cash)} accent="sky" icon={Building2} hint={`AR: ${fmt(bs.assets.accounts_receivable)} · AP: ${fmt(bs.liabilities.accounts_payable)}`} />
      </div>

      {/* Secondary KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat label="Cash Inflows" value={fmt(cf?.total_inflows || 0)} accent="emerald" icon={ArrowUpRight} hint="This month" />
        <Stat label="Cash Outflows" value={fmt(cf?.total_outflows || 0)} accent="rose" icon={TrendingDown} hint="Expenses + bills paid" />
        <Stat label="Tax Payable (MTD)" value={fmt(tax?.net_tax_payable || 0)} accent="amber" icon={Percent} hint={`Output: ${fmt(tax?.output_tax || 0)} − Input: ${fmt(tax?.input_tax || 0)}`} />
        <Stat label="Open Bills" value={data.open_bills} accent="violet" icon={FileText} hint={`${data.vendor_count} vendors`} />
      </div>

      {/* Quick links to submodules */}
      <div className="mb-6">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Submodules</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <QuickLink to="/finance/expenses" icon={Receipt} label="Expenses" hint={`${data.expense_count_mtd} this month`} />
          <QuickLink to="/finance/bills" icon={FileText} label="Bills & Vendors" hint={`${data.open_bills} open · ${data.vendor_count} vendors`} />
          <QuickLink to="/finance/reports" icon={BarChart3} label="P&L / Balance Sheet" hint="Profit, cash flow, AR/AP aging" />
          <QuickLink to="/finance/tax" icon={Percent} label="Tax & VAT" hint={`Net payable: ${fmt(tax?.net_tax_payable || 0)}`} />
        </div>
      </div>

      {/* Side-by-side AR / AP aging */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center justify-between">Accounts Receivable <Link to="/finance/reports" className="text-xs font-normal text-blue-600 hover:underline">View report →</Link></CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(aging.buckets).map(([bucket, amount]) => (
                <div key={bucket} className={`p-2.5 rounded-md border ${bucket === '90+' && amount > 0 ? 'border-rose-300 bg-rose-50' : 'border-gray-200'}`} data-testid={`ar-aging-${bucket}`}>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">{bucket} days</div>
                  <div className="text-base font-bold mt-1">{fmt(amount)}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex justify-between items-center text-sm pt-2 border-t border-gray-100">
              <span className="text-gray-600">Total outstanding</span>
              <span className="font-bold">{fmt(aging.total_outstanding)}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center justify-between">Accounts Payable <Link to="/finance/bills" className="text-xs font-normal text-blue-600 hover:underline">Open bills →</Link></CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(payables.buckets).map(([bucket, amount]) => (
                <div key={bucket} className={`p-2.5 rounded-md border ${bucket === '90+' && amount > 0 ? 'border-rose-300 bg-rose-50' : 'border-gray-200'}`} data-testid={`ap-aging-${bucket}`}>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">{bucket} days</div>
                  <div className="text-base font-bold mt-1">{fmt(amount)}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex justify-between items-center text-sm pt-2 border-t border-gray-100">
              <span className="text-gray-600">Total payable</span>
              <span className="font-bold">{fmt(payables.total_outstanding)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expense breakdown */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center justify-between">Expense breakdown — month to date <Link to="/finance/reports" className="text-xs font-normal text-blue-600 hover:underline">Full P&L →</Link></CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {pl.expense_breakdown.length === 0 && <p className="text-sm text-gray-400">No expenses logged for this period yet.</p>}
            {pl.expense_breakdown.map(b => {
              const pct = pl.total_expenses ? (b.amount / pl.total_expenses) * 100 : 0;
              return (
                <div key={b.account_code} data-testid={`pl-row-${b.account_code}`}>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-700">{b.name}</span>
                    <span className="font-mono text-gray-900">{fmt(b.amount)}</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded overflow-hidden mt-1">
                    <div className="h-full bg-rose-400" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          {aging.buckets['90+'] > 0 && (
            <div className="mt-4 flex items-start gap-2 p-2.5 rounded bg-rose-50 border border-rose-200 text-xs text-rose-800">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>You have invoices outstanding over 90 days. Consider triggering an automated payment reminder.</span>
            </div>
          )}
        </CardContent>
      </Card>

      <ModuleAutomationsPanel module="finance" title="Finance Automations" />
    </div>
  );
}
